import { Match, Show, Switch, createResource, createSignal, onCleanup } from "solid-js";
import type { TeacherClass } from "~/features/classes/classes";
import { VotingResetModal } from "./VotingResetModal";
import { VotingResultsPanel } from "./VotingResultsPanel";
import { VotingStartPanel } from "./VotingStartPanel";
import type { VotingCommand, VotingModule, VotingSnapshot } from "./voting";

function className(value: TeacherClass) {
  const school = value.teacher_school_name || value.school_name;
  return school ? `${value.name} · ${school}` : value.name;
}

export function VotingPage(props: { value?: TeacherClass; module: VotingModule; onNavigateGoals: () => void; onChanged?: () => void }) {
  const [snapshot, { refetch }] = createResource(() => props.value?.id, (classId) => props.module.load(classId));
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [message, setMessage] = createSignal("");
  const [confirmingClose, setConfirmingClose] = createSignal(false);
  const [resetting, setResetting] = createSignal(false);
  let messageTimer: number | undefined;

  const flash = (value: string) => {
    setMessage(value);
    if (messageTimer) window.clearTimeout(messageTimer);
    messageTimer = window.setTimeout(() => setMessage(""), 3_500);
  };
  onCleanup(() => messageTimer && window.clearTimeout(messageTimer));

  const execute = async (command: VotingCommand, fallback: string) => {
    if (!props.value || busy()) return;
    setBusy(true); setError(""); setMessage("");
    try {
      await props.module.execute(props.value.id, command);
      setConfirmingClose(false); setResetting(false);
      await refetch();
      props.onChanged?.();
      flash(fallback);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Abstimmung konnte nicht aktualisiert werden.");
    } finally {
      setBusy(false);
    }
  };

  return <Show when={props.value} fallback={<div class="card"><p>Bitte wähle zuerst eine Klasse.</p></div>}>{(selectedClass) => <>
    <Show when={error()}><div class="form-error" role="alert" style={{ "margin-bottom": "12px" }}>{error()}</div></Show>
    <Show when={message()}><div class="toast-container"><div class="toast success" role="status">{message()}</div></div></Show>
    <Show when={snapshot()} fallback={<Show when={snapshot.error} fallback={<div style={{ display: "flex", "justify-content": "center", padding: "40px" }}><div class="loading-spinner" /></div>}><div class="card"><p>Fehler beim Laden der Abstimmung</p><button class="btn btn-primary" type="button" style={{ "margin-top": "12px" }} onClick={() => void refetch()}>Erneut versuchen</button></div></Show>}>{(data) => <Switch>
      <Match when={data().kind === "start"}>{(() => { const value = data() as Extract<VotingSnapshot, { kind: "start" }>; return <VotingStartPanel className={className(selectedClass())} goals={value.goals} lastWeekGoals={value.lastWeekGoals} busy={busy()} onNavigateGoals={props.onNavigateGoals} onStart={(goalIds) => execute({ type: "start", goalIds }, "Abstimmung gestartet!")} />; })()}</Match>
      <Match when={data().kind === "active"}>{(() => { const value = data() as Extract<VotingSnapshot, { kind: "active" }>; return <VotingResultsPanel className={className(selectedClass())} value={value} busy={busy()} confirmingClose={confirmingClose()} onConfirmingCloseChange={setConfirmingClose} onCloseAndActivate={() => execute({ type: "close-and-activate", sessionId: value.session.id }, "Wochen-Klassenziele gesetzt!")} onActivate={() => execute({ type: "activate", sessionId: value.session.id }, "Wochen-Klassenziele gesetzt!")} onReset={() => setResetting(true)} />; })()}</Match>
    </Switch>}</Show>
    <Show when={resetting() && snapshot()?.kind === "active"}><VotingResetModal busy={busy()} onClose={() => setResetting(false)} onConfirm={() => execute({ type: "reset", sessionId: (snapshot() as Extract<VotingSnapshot, { kind: "active" }>).session.id }, "Abstimmung zurückgesetzt")} /></Show>
  </>}</Show>;
}
